import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, Cake, Calendar, Bell, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';

export function BirthdaysPlugin({ serverId }: { serverId: string }) {
  const { t } = useI18n();
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const birthdaysPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'birthdays');
  const isEnabled = birthdaysPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) => 
      enabled ? pluginApi.enablePlugin(serverId, 'birthdays') : pluginApi.disablePlugin(serverId, 'birthdays'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'birthdays', config),
    { 
      onSuccess: () => toast.success(t('birthdays.saved')),
      invalidateQueries: [['servers', serverId, 'plugins']] 
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    announcementChannelId: '',
    announcementMessage: '🎉 Happy Birthday {user}! 🎂',
    announcementTime: '09:00',
    pingUser: true,
    customRole: '',
    showAge: true,
    privateReminders: false,
    reminderDaysBefore: 1
  });

  useEffect(() => {
    if (birthdaysPlugin?.config) {
      setConfig({ ...config, ...birthdaysPlugin.config });
    }
  }, [birthdaysPlugin]);

  const handleSave = () => {
    updateConfig(config);
  };

  if (!birthdaysPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-pink-500/20 rounded-xl">
            <Cake className="w-6 h-6 text-pink-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Birthdays</h3>
            <p className="text-sm text-muted-foreground">
              {t('birthdays.subtitle')}
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <>
          {/* Announcement Settings */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-5 h-5 text-pink-400" />
              <h4 className="font-semibold">{t('birthdays.announcementSettings')}</h4>
            </div>

            <div className="space-y-4">
              <div>
                <Label>{t('birthdays.announcementChannel')}</Label>
                <Select
                  value={config.announcementChannelId || 'none'}
                  onValueChange={(value) => setConfig({ ...config, announcementChannelId: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('birthdays.selectAnnouncementChannel')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('birthdays.noChannel')}</SelectItem>
                    {(channels as any[]).map((channel: any) => (
                      <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('birthdays.announcementChannelHelp')}
                </p>
              </div>

              <div>
                <Label>{t('birthdays.announcementMessage')}</Label>
                <Input
                  placeholder={t('birthdays.announcementMessagePlaceholder')}
                  value={config.announcementMessage}
                  onChange={(e) => setConfig({ ...config, announcementMessage: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('birthdays.announcementMessageHelp')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('birthdays.announcementTime')}</Label>
                  <Input
                    type="time"
                    value={config.announcementTime}
                    onChange={(e) => setConfig({ ...config, announcementTime: e.target.value })}
                  />
                </div>

                <div>
                  <Label>{t('birthdays.reminderDaysBefore')}</Label>
                  <Select
                    value={config.reminderDaysBefore.toString()}
                    onValueChange={(value) => setConfig({ ...config, reminderDaysBefore: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">{t('birthdays.sameDay')}</SelectItem>
                      <SelectItem value="1">{t('birthdays.oneDayBefore')}</SelectItem>
                      <SelectItem value="3">{t('birthdays.threeDaysBefore')}</SelectItem>
                      <SelectItem value="7">{t('birthdays.oneWeekBefore')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-pink-400" />
                  <div>
                    <Label className="text-sm">{t('birthdays.pingUser')}</Label>
                    <p className="text-xs text-muted-foreground">{t('birthdays.pingUserDesc')}</p>
                  </div>
                </div>
                <Switch
                  checked={config.pingUser}
                  onCheckedChange={(checked) => setConfig({ ...config, pingUser: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-pink-400" />
                  <div>
                    <Label className="text-sm">{t('birthdays.showAge')}</Label>
                    <p className="text-xs text-muted-foreground">{t('birthdays.showAgeDesc')}</p>
                  </div>
                </div>
                <Switch
                  checked={config.showAge}
                  onCheckedChange={(checked) => setConfig({ ...config, showAge: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-pink-400" />
                  <div>
                    <Label className="text-sm">{t('birthdays.privateReminders')}</Label>
                    <p className="text-xs text-muted-foreground">{t('birthdays.privateRemindersDesc')}</p>
                  </div>
                </div>
                <Switch
                  checked={config.privateReminders}
                  onCheckedChange={(checked) => setConfig({ ...config, privateReminders: checked })}
                />
              </div>
            </div>
          </div>

          {/* Commands Info */}
          <div className="glass-card p-6 space-y-4">
            <h4 className="font-semibold mb-2">{t('birthdays.availableCommands')}</h4>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-pink-400">/set-birthday [day] [month] [year]</code>
                <p className="text-muted-foreground mt-1">{t('birthdays.setBirthday')}</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-pink-400">/birthday [@user]</code>
                <p className="text-muted-foreground mt-1">{t('birthdays.checkBirthday')}</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-pink-400">/upcoming-birthdays</code>
                <p className="text-muted-foreground mt-1">{t('birthdays.upcomingBirthdays')}</p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('birthdays.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {t('birthdays.save')}
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
